import { useState, type MouseEvent } from 'react';

import { Box, Chip, Divider, Menu, MenuItem } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import {
  STATUSES,
  STATUS_COLOR,
  type Status,
} from '@/cam-admin/care-search/CareSearch.content';

// Lifted out of CareSearch so the shortlists tab can use it too. It was a local
// there while care search was the only place a CAM set a home's status; the
// tab shows the same four values, so leaving it there would have meant either a
// second menu or an import cycle — the tab's StatusChip is what care search
// renders for a dom row.

const badgeSx = { height: 20 } as const;

// The chip's dropdown affordance. MUI clones a chip's deleteIcon with a
// className (its slot positioning) and an onClick (the onDelete handler) — the
// llama Icon forwards neither, so wrap it in a span that takes both and
// inline-flex centres the chevron, fixing the alignment.
const ChipChevron = ({
  className,
  onClick,
}: {
  className?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
}) => (
  <Box
    sx={{ display: 'inline-flex', alignItems: 'center' }}
    component='span'
    className={className}
    onClick={onClick}
  >
    <Icon icon='chevron-down' fontSize={12} />
  </Box>
);

ChipChevron.displayName = 'ChipChevron';

// Tappable status badge + its menu (statuses plus Dismissed), shared by the
// full shortlist cards, the compact basket rows and the shortlists tab.
export const StatusChipMenu = ({
  status,
  onStatus,
  onDismiss,
  color,
}: {
  status: string;
  onStatus: (s: string) => void;
  /**
   * Dismissing, where the surface has somewhere for it to go. Care search opens
   * its reason dialog; omit it and the menu is the four statuses on their own,
   * rather than offering an act the surface cannot carry out.
   */
  onDismiss?: () => void;
  /**
   * The chip colour, where the surface has its own reading of these four. The
   * shortlists tab does: it colours them off the brand ramps, matching the dom
   * chip in the same column, while care search still uses MUI's info/success/
   * error. Two palettes for one status set is a thing to settle, not a thing to
   * bake in — hence a prop rather than a fork.
   */
  color?: 'limeGreen' | 'sunflower' | 'warmOrange' | 'default';
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Chip
        label={status}
        size='small'
        sx={badgeSx}
        color={color ?? STATUS_COLOR[status as Status] ?? 'default'}
        // A control inside a clickable parent: the shortlists tab opens a row
        // drawer on row click, so without this, setting a status also opens the
        // drawer of the row you set it on. Care search's cards are tappable the
        // same way.
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        onDelete={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget as HTMLElement);
        }}
        deleteIcon={<ChipChevron />}
      />
      {/* The menu portals to the body, but React events bubble through the
          component tree rather than the DOM one, so a click on an item still
          reaches the row underneath and opens its drawer. Stopping it here
          covers the items and the backdrop in one place. */}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {STATUSES.map((s) => (
          <MenuItem
            key={s}
            selected={s === status}
            onClick={() => {
              onStatus(s);
              setAnchor(null);
            }}
          >
            {s}
          </MenuItem>
        ))}
        {/* dismissing is a status choice too — it still captures a reason
            via the dialog */}
        {onDismiss && <Divider />}
        {onDismiss && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onDismiss();
            }}
          >
            Dismissed
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

StatusChipMenu.displayName = 'StatusChipMenu';
export default StatusChipMenu;
